<script setup lang="ts">
  import { useScrollDirection } from '../../../composables/useScrollDirection'
  import type { ComputedRef } from 'vue'
  import type { FooterProps } from '../../Footer/Footer.vue'
  import type {
    ArticleGroupDetails,
    DeepPartial,
  } from '../../../composables/feature-flags/index.types'
  import type { SiderailArticleGroupProps } from '../../ArticleSiderail/ArticleSiderail.vue'
  import { createArticleGroup } from '../../../utils/layout/createArticleGroup'

  const contentDisplaySettingsStore = useContentDisplaySettingsStore()
  const { featureFlags } = storeToRefs(contentDisplaySettingsStore)
  const pageDesign = computed(
    () => featureFlags?.value.summaryPage?.design ?? 'google'
  )

  const { selectedCategory, selectedLang } = useSearchParams()
  const { settings } = useGlobalLayoutSettings()
  const { scrollDirection } = useScrollDirection()
  const { headlines } = useNewsArticleHeadlines(selectedCategory)

  const topArticleGroup: ComputedRef<SiderailArticleGroupProps | undefined> =
    computed(() => {
      const topGroupFeatureFlags: DeepPartial<ArticleGroupDetails | undefined> =
        featureFlags?.value.summaryPage?.siderail?.topArticleGroup

      return createArticleGroup(
        headlines.value,
        topGroupFeatureFlags,
        pageDesign.value
      )
    })

  const bottomArticleGroup: ComputedRef<SiderailArticleGroupProps | undefined> =
    computed(() => {
      const bottomGroupFeatureFlags: DeepPartial<
        ArticleGroupDetails | undefined
      > = featureFlags?.value.summaryPage?.siderail?.bottomArticleGroup
      const articleIds = topArticleGroup.value?.articles
        .map((article) => article.id)
        .filter((id): id is string => id !== undefined)

      return createArticleGroup(
        headlines.value,
        bottomGroupFeatureFlags,
        pageDesign.value,
        articleIds
      )
    })

  const footerProps = computed<FooterProps>(() => {
    return {
      footerPosition: scrollDirection.value === 'up' ? 'fixed' : 'static',
      title: settings.siteName,
    }
  })

  async function returnToHomePage(
    category: string,
    lang: string | string[] | undefined
  ) {
    const { settings } = useGlobalLayoutSettings()
    await navigateTo({
      path: settings.homePageUrl,
      query: { category, lang },
    })
  }

  usePageViewTracker()

  const isMenuOpen = ref(false)

  const adPrefix = useAppConfig().siteAdPrefix
  await contentDisplaySettingsStore.initialized
</script>
<template>
  <div
    class="flex h-full flex-col bg-background-default dark:bg-background-neutral-subtle"
  >
    <Navbar
      v-model:menu-open="isMenuOpen"
      :logo-image="settings?.headerLogo"
      :tagline="settings?.tagline"
      :logo-image-dark-mode="settings?.headerLogoDarkMode"
    />
    <Menu
      v-model:menu-open="isMenuOpen"
      v-model:selected-category="selectedCategory"
      class="top-[72px]"
      @tab-category-click="
        () => returnToHomePage(selectedCategory, selectedLang)
      "
    />
    <Tab
      v-model:selected="selectedCategory"
      :tabs="settings.tabCategories"
      :design="pageDesign"
      :set-max-width="false"
      class="border-b border-gray-300 dark:border-background-neutral-muted pl-[max(50px,_calc((100%_-_1236px)_/2))] pb-12px"
      @tab-click="() => returnToHomePage(selectedCategory, selectedLang)"
    />
    <div
      v-if="featureFlags.summaryPage?.showContentHeroAdPlacement"
      :class="`${adPrefix}_article_hero`"
      class="ad-placement bg-background-default dark:bg-background-neutral-subtle"
    />
    <div
      :class="{
        'bg-background-neutral-subtle dark:bg-background-neutral-surface':
          pageDesign === 'google',
        'bg-background-default': pageDesign === 'msn',
      }"
      class="flex justify-center px-[50px]"
    >
      <div
        :class="{
          'grid max-w-[1236px] py-10 grid-cols-[1fr_300px] max-lg:grid-cols-[1fr] gap-x-3':
            pageDesign === 'google',
          'grid max-w-[846px] 2xl:max-w-[1236px] py-10 grid-cols-[1fr] 2xl:grid-cols-[1fr_300px] 2xl:gap-x-[90px]':
            pageDesign === 'msn',
        }"
      >
        <div
          :class="{
            'col-span-2':
              featureFlags.summaryPage?.siderail?.position === 'below-header' ||
              !featureFlags.summaryPage?.siderail?.position,
          }"
        >
          <slot name="article-summary-header"></slot>
        </div>
        <div class="row-start-2 col-start-1">
          <slot name="article-summary-body" />
        </div>
        <div
          :class="{
            'row-start-2':
              featureFlags.summaryPage?.siderail?.position === 'below-header' ||
              !featureFlags.summaryPage?.siderail?.position,
            'row-span-2 col-start-2 row-start-1':
              featureFlags.summaryPage?.siderail?.position === 'full-height',
          }"
        >
          <ArticleSiderail
            :top-group="topArticleGroup"
            :bottom-group="bottomArticleGroup"
            class="hidden 2xl:flex"
          />
        </div>
      </div>
    </div>
    <Footer v-bind="footerProps" />
  </div>
</template>
